import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { selectCurrentAccount, selectUserId } from "fond/api";
import { startAccountSwitch } from "fond/redux/accountContextSlice";
import { setAccountContext } from "fond/utils/accountContextStorage";
import { useAppDispatch } from "fond/utils/hooks";
import { ConfirmModal } from "fond/widgets";
import { AccountSwitchDisplay } from "fond/widgets/AccountSwitch";

type PendingAccountSwitch = {
  id: string;
  name: string;
} | null;

type UseAccountSwitchingOptions = {
  onConfirm?: () => void;
};

/**
 * Custom hook to handle account switching with confirmation modal.
 * Provides an `initiateSwitch` function to trigger the switch process and a `modal` component to be rendered in the UI.
 * The `initiateSwitch` function takes the target account ID and name, and shows a confirmation modal.
 * If the user confirms, it updates the account context and navigates to the workspace.
 * If the user cancels, it simply closes the modal without switching accounts.
 */
export const useAccountSwitching = (
  options?: UseAccountSwitchingOptions
): { initiateSwitch: (accountId: string, name: string) => void; modal: JSX.Element | null } => {
  const [pendingAccountSwitch, setPendingAccountSwitch] = useState<PendingAccountSwitch>(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userId = useSelector(selectUserId);
  const currentAccount = useSelector(selectCurrentAccount);

  const initiateSwitch = useCallback(
    (accountId: string, name: string) => {
      // Navigate to workspace if clicking current account
      if (accountId === currentAccount?.ID) {
        navigate("/workspace");
        return;
      }
      // Show confirmation modal before switching
      setPendingAccountSwitch({ id: accountId, name });
    },
    [currentAccount?.ID, navigate]
  );

  const confirmSwitch = useCallback(() => {
    if (userId && pendingAccountSwitch) {
      // Call optional onConfirm callback before switching (e.g., for showing notifications)
      options?.onConfirm?.();
      // Signal switch in progress so EntityAccessGuard skips its redirect during the transition
      dispatch(startAccountSwitch());
      setAccountContext(userId, pendingAccountSwitch.id);
      // Use replace to navigate to root and force full page reload
      window.location.replace("/workspace");
    }
  }, [userId, pendingAccountSwitch, options, dispatch]);

  const cancelSwitch = useCallback(() => {
    setPendingAccountSwitch(null);
  }, []);

  const modal = pendingAccountSwitch ? (
    <ConfirmModal
      open
      onConfirm={confirmSwitch}
      onCancel={cancelSwitch}
      header="Switch Account"
      content={<AccountSwitchDisplay currentAccountName={currentAccount?.Name || ""} targetAccountName={pendingAccountSwitch.name} />}
      confirmText="Switch account"
      cancelText="Cancel"
    />
  ) : null;

  return {
    initiateSwitch,
    modal,
  };
};
